import Form                    from '@widesk-ui/components/form/Form';
import copyToClipboard         from '@widesk-ui/tools/copyToClipboard';
import { CopyOutlined }        from '@ant-design/icons';
import { message }             from '@widesk-ui/hooks/useMessage';
import Input                   from '@widesk-ui/components/form/Input';
import ProjectModel            from '@models/map/ProjectModel';
import publicProjectLink       from '@/tools/publicProjectLink';
import projectPublicationStore from '@/TokamapEditor/stores/projectPublicationStore';
import useModal                from '@widesk-ui/hooks/useModal';
import React                   from 'react';
import Radio                   from '@widesk-ui/components/form/Radio';
import View                    from '@widesk-ui/components/View';
import Divider                 from '@widesk-ui/components/Divider';
import InputNumber             from '@widesk-ui/components/form/InputNumber';
import useDesignTokens         from '@/hooks/useDesignTokens';
import Space                   from '@widesk-ui/components/Space';
import Select                  from '@widesk-ui/components/form/Select';
import { renderToString }      from 'react-dom/server';
import { FormTools }           from '@widesk-ui/components/form/Form';
import { FormFields }          from '@widesk-ui/components/form/Form';
import useTheme                from '@widesk-ui/hooks/useTheme';
import SliderInputNumber       from '@/TokamapEditor/ux/AreaSidebar/SliderInputNumber';
import Button                  from '@widesk-ui/components/Button';
import { MinusCircleFilled }   from '@ant-design/icons';
import FunctionalityGate       from '@/components/Functionality';
import { PlusCircleFilled }    from '@ant-design/icons';
import InputBoolean            from '@widesk-ui/components/form/InputBoolean';
import ColorPicker             from '@widesk-ui/components/form/ColorPicker';
import LogoLoader              from '@/components/LogoLoader';
import { observer }            from 'mobx-react-lite';
import TextAreaWithCopy        from '@widesk-ui/components/form/TextAreaWithCopy';

const IFrame = (props: { publicLink: string, formTools: FormTools<FormFields>, onLoaded?: () => void }) => {
	const iframeTitle = props.formTools.getFieldValue('iframeTitle');
	const iframeFullscreenable = props.formTools.getFieldValue('iframeFullscreenable');
	const iframeWidth = props.formTools.getFieldValue('iframeWidth') || '100%';
	const iframeHeight = props.formTools.getFieldValue('iframeHeight') || '100%';
	const iframeBorderRadius = props.formTools.getFieldValue('iframeBorderRadius');
	const iframeBorder = props.formTools.getFieldValue('iframeBorder');

	React.useEffect(() => {
		setTimeout(() => props.onLoaded?.(), 1000);
	}, []);

	return (
		<iframe
			onLoad={props.onLoaded ? () => setTimeout(() => props.onLoaded?.(), 750) : undefined}
			id="tokamap-map-viewer"
			allowFullScreen={iframeFullscreenable as unknown as boolean}
			onClick={() => console.log('test')}
			src={props.publicLink}
			style={{
				display: 'block',
				justifySelf: 'center',
				height: iframeHeight,
				width: iframeWidth,
				border: iframeBorder,
				boxSizing: 'border-box',
				borderRadius: iframeBorderRadius,
			}}
			title={iframeTitle}
		/>
	);
};

const IFramePreviewHeight = 379.125;
const IFramePreviewWidth = 674;
const IFramePreview = (props: { publicLink: string, formTools: FormTools<FormFields> }) => {
	const [zoom, setZoom] = React.useState<number>(0.65);
	const [loading, setLoading] = React.useState(true);
	const [iframeKey, setIframeKey] = React.useState<React.Key>(zoom.toString());
	const token = useDesignTokens();
	const theme = useTheme();

	return (
		<View gapV="xs">
			<View bold centerV h={32}>Aperçu</View>
			<div>
				<View
					gapV="xs"
					bg={theme.colorBgContainer}
					br={theme.borderRadius}
					paddingV="xs"
					paddingH="sm"
					style={{ border: `1px solid ${theme.colorBorder}` }}
				>
					<div style={{
						overflow: 'hidden',
						height: `calc(${IFramePreviewHeight}px - 2px)`,
						outline: `1px dashed ${token.color.primary}`,
						margin: 1,
					}}>
						<div style={{ height: '100%', overflow: 'hidden', position: 'relative', zoom }}>
							{loading && (
								<View abs absT={0} absL={0} w="100%" h="100%" bg="white" zIndex={10} flex center>
									<LogoLoader size={64} />
								</View>
							)}
							<IFrame {...props} key={iframeKey} onLoaded={() => setLoading(false)} />
						</div>
					</div>
					<View centerV gapH="xs" spread>
						<View row spread>
							<span>Zoom de l'aperçu</span>
							<View color={token.text.muted} size={theme.sizeSM}>
								Zone d'affichage de {(IFramePreviewWidth / zoom).toFixed(2)}px (L) * {(IFramePreviewHeight / zoom).toFixed(2)}px (H)
							</View>
						</View>
						<SliderInputNumber
							max={1.6}
							min={0.1}
							onAfterChange={v => {
								setLoading(true);
								setIframeKey((v as number).toString());
							}}
							onChange={v => setZoom(v as number)}
							size="small"
							step={0.01}
							value={zoom}
						/>
					</View>
				</View>
			</div>
		</View>
	);
};

type ShareMode = 'publicLink' | 'iframe';
type ShareFormProps = {
	project: ProjectModel;
};

/**
 * Le partage porte sur le PROJET : une seule adresse publique, celle de son slug, qui sert toutes
 * ses cartes. Elle ne répond avec du contenu qu'une fois le projet publié — d'où l'avertissement
 * tant qu'aucune publication n'est allée au bout.
 */
const ShareForm = observer(({ project }: ShareFormProps) => {
	const { modalStore } = useModal();
	const [shareMode, setShareMode] = React.useState<ShareMode>('publicLink');
	const [iframePreview, setIframePreview] = React.useState<Element>();
	const [showOptions, setShowOptions] = React.useState(false);
	const publicLink = React.useMemo(() => publicProjectLink(project), [project]);

	// Tant qu'aucune publication n'est allée au bout, la route publique du projet répond bien, mais
	// sans aucune carte : le lien est déjà valide et déjà partageable, il ne montre simplement rien.
	const notPublishedYet = projectPublicationStore.isLoaded && !projectPublicationStore.isPublishedOnce;

	React.useEffect(() => {
		if (modalStore) {
			modalStore.setAdditionalProps({
				okText: `Copier ${shareMode === 'publicLink' ? 'le lien' : `le code d'intégration`}`,
			});
		}
	}, [shareMode]);

	return (
		<Form
			onDidMount={async formTools => formTools.setFieldsValue({
				shareMode: 'publicLink',
				publicLink,
				iframeTitle: 'Tokamap map viewer',
				iframeWidth: '100%',
				iframeHeight: '100%',
				iframeBorderRadius: 'none',
				iframeBorder: 'none',
			})}
			onChange={formTools => {
				const iframeCode = renderToString(<IFrame publicLink={publicLink} formTools={formTools} />);
				formTools.setFieldValue('iframeCode', iframeCode);
				setIframePreview(iframePreview);
			}}
			fields={formTools => ({
				shareMode: {
					label: 'Mode de partage',
					initialValue: 'publicLink',
					element: (
						<Radio.Group
							onChange={e => setShareMode(e.target.value as ShareMode)}
							value={shareMode}
						>
							<Radio.Button value="publicLink">Lien public</Radio.Button>
							<FunctionalityGate reference="iframe_limit">
								<Radio.Button value="iframe">Intégration</Radio.Button>
							</FunctionalityGate>
						</Radio.Group>
					),
					span: 24,
					required: true,
				},
				publicLink: {
					hidden: shareMode !== 'publicLink',
					label: 'Lien public',
					span: 24,
					initialValue: publicLink,
					extra: notPublishedYet
						? `Ce projet n'a pas encore été publié : ce lien ne montrera rien tant que la première publication n'est pas terminée.`
						: undefined,
					element: (
						<Input
							disabled
							suffix={(
								<CopyOutlined
									onClick={async () => {
										await navigator.clipboard.writeText(publicLink);
										message.open({ type: 'success', content: 'Lien copié dans le presse-papier.', duration: 2 });
									}}
								/>
							)}
						/>
					),
				},
				iframePreview: {
					hidden: shareMode !== 'iframe',
					element: <IFramePreview publicLink={publicLink} formTools={formTools} />,
					noFormItem: true,
					span: 24,
				},
				iframeOptionsDivider: {
					hidden: shareMode !== 'iframe',
					element: (
						<Divider size="small">
							<View centerV gapH="xs" row>
								Options
								<Button
									size="small"
									icon={showOptions ? <MinusCircleFilled /> : <PlusCircleFilled />}
									onClick={() => setShowOptions(!showOptions)}
								>
									{showOptions ? 'Fermer' : 'Ouvrir'}
								</Button>
							</View>
						</Divider>
					),
					span: 24,
				},
				iframeTitle: {
					hidden: shareMode !== 'iframe' || !showOptions,
					label: 'Titre',
					initialValue: 'Tokamap map viewer',
					element: <Input />,
					span: 12,
					extra: `Utilisé par les lecteurs d'écran a des fins d'accessibilité`,
					required: true,
				},
				iframeFullscreenable: {
					hidden: shareMode !== 'iframe' || !showOptions,
					label: 'Activer plein écran',
					initialValue: true,
					element: <InputBoolean />,
					span: 6,
					required: true,
				},
				iframeWidth: {
					hidden: shareMode !== 'iframe' || !showOptions,
					label: 'Largeur',
					initialValue: '100%',
					element: <IFrameDimensionInput />,
					span: 24,
					required: true,
					rules: IFrameDimensionInputRules,
				},
				iframeHeight: {
					hidden: shareMode !== 'iframe' || !showOptions,
					label: 'Hauteur',
					initialValue: '100%',
					element: <IFrameDimensionInput />,
					span: 24,
					required: true,
					rules: IFrameDimensionInputRules,
				},
				iframeBorderRadius: {
					hidden: shareMode !== 'iframe' || !showOptions,
					label: 'Arrondi',
					initialValue: 'none',
					element: <IFrameBorderRadiusInput />,
					span: 24,
					required: true,
					rules: IFrameBorderRadiusInputRules,
				},
				iframeBorder: {
					hidden: shareMode !== 'iframe' || !showOptions,
					label: 'Bordure',
					initialValue: 'none',
					element: <IFrameBorderInput />,
					span: 24,
					required: true,
					rules: IFrameBorderInputRules,
				},
				iframeCode: {
					hidden: shareMode !== 'iframe',
					label: `Code d'intégration`,
					element: (
						<TextAreaWithCopy
							copyMessage="Code d'intégration copié"
							rows={4}
							style={{ resize: 'none' }}
						/>
					),
					span: 24,
				},
			})}
			onSubmit={async values => {
				const value = (shareMode === 'publicLink' ? values.publicLink : values.iframeCode) as string;
				const successLabel = shareMode === 'publicLink' ? 'Lien' : `Code d'intégration`;
				return copyToClipboard(value, `${successLabel} copié`);
			}}
			updateOnChange
		/>
	);
});

type SelectCssUnit = {
	disabled?: boolean;
	onChange?: (value?: unknown) => void;
	value?: string;
};
const SelectCssUnit = ({ disabled, onChange, value }: SelectCssUnit) => (
	<Select
		labelRender={o => (o.label as string).split(' ')?.at(0)}
		disabled={disabled}
		onChange={onChange}
		options={[
			{ value: '%', label: `% (pourcentage du parent)` },
			{ value: 'px', label: 'px (pixels)' },
			{ value: 'vw', label: 'vw (largeur de la fenêtre)' },
			{ value: 'vh', label: `vh (hauteur de la fenêtre)` },
		]}
		placeholder="Unité"
		style={{ width: 80 }}
		value={value}
	/>
);

type IFrameOptionInputProps = {
	placeholder?: string;
	onChange?: (v?: string) => void;
	value?: string;
}

const IFrameDimensionInput = (props: IFrameOptionInputProps) => {
	const { onChange, value } = props;

	const [mode, setMode] = React.useState<'auto' | 'custom' | 'minimal'>('auto');
	React.useEffect(() => {
		let value = undefined;
		switch (mode) {
			case 'auto':
				value = '100%';
				break;
			case 'minimal':
				value = props.placeholder === 'Largeur' ? '300px' : '400px';
				break;
		}

		onChange?.(value);
	}, [mode]);

	const sizeValue = value?.match(/\d+/)?.at(0);
	const unitValue = value === 'none' ? undefined : value?.match(/[a-zA-Z%]+/)?.at(0);

	return (
		<View gap="sm" row spread>
			<Radio.Group
				onChange={e => setMode(e.target.value)}
				value={mode}
			>
				<Radio.Button value="auto">Automatique</Radio.Button>
				<Radio.Button value="minimal">Minimale</Radio.Button>
				<Radio.Button value="custom">Personnalisé</Radio.Button>
			</Radio.Group>
			<Space.Compact>
				<InputNumber
					disabled={mode !== 'custom'}
					changeOnWheel
					max={9999}
					min={1}
					onChange={v => onChange?.(`${v as string}${unitValue || ''}`)}
					placeholder="Taille"
					style={{ width: 100 }}
					value={sizeValue as number | undefined}
				/>
				<SelectCssUnit
					disabled={mode !== 'custom'}
					onChange={v => onChange?.(`${sizeValue || ''}${v}`)}
					value={unitValue}
				/>
			</Space.Compact>
		</View>
	);
};
const IFrameDimensionInputRules = [{
	pattern: /\d+[a-zA-Z%]+/,
	message: 'Veuillez renseigner cette information',
}];

const IFrameBorderRadiusInput = (props: IFrameOptionInputProps) => {
	const { onChange, value } = props;
	const theme = useTheme();

	const [mode, setMode] = React.useState<'none' | 'sm' | 'md' | 'lg' | 'custom'>('none');
	React.useEffect(() => {
		let value: string | undefined;
		switch (mode) {
			case 'none':
				value = `none`;
				break;
			case 'sm':
				value = `${theme.borderRadius / 2}px`;
				break;
			case 'md':
				value = `${theme.borderRadius}px`;
				break;
			case 'lg':
				value = `${theme.borderRadius * 2}px`;
				break;
		}
		onChange?.(value);
	}, [mode]);

	const sizeValue = value?.match(/\d+/)?.at(0);
	const unitValue = value === 'none' ? undefined : value?.match(/[a-zA-Z%]+/)?.at(0);

	return (
		<View gap="sm" row spread>
			<Radio.Group
				onChange={e => setMode(e.target.value)}
				value={mode}
			>
				<Radio.Button value="none">Aucun</Radio.Button>
				<Radio.Button value="sm">Petit</Radio.Button>
				<Radio.Button value="md">Moyen</Radio.Button>
				<Radio.Button value="lg">Grand</Radio.Button>
				<Radio.Button value="custom">Personnalisé</Radio.Button>
			</Radio.Group>
			{mode !== 'none' && (
				<Space.Compact>
					<InputNumber
						disabled={mode !== 'custom'}
						changeOnWheel
						max={9999}
						min={1}
						onChange={v => onChange?.(`${v as string}${unitValue || ''}`)}
						placeholder="Taille"
						style={{ width: 100 }}
						value={sizeValue as number | undefined}
					/>
					<SelectCssUnit
						disabled={mode !== 'custom'}
						onChange={v => onChange?.(`${sizeValue || ''}${v}`)}
						value={unitValue}
					/>
				</Space.Compact>
			)}
		</View>
	);
};
const IFrameBorderRadiusInputRules = [{
	pattern: /none|(\d+[a-zA-Z%]+)/,
	message: 'Veuillez renseigner cette information',
}];

const IFrameBorderInput = (props: IFrameOptionInputProps) => {
	const { onChange, value } = props;
	const isEnabled = value !== 'none';

	const sizeValue = value?.match(/^\d+/)?.at(0);
	const styleValue = value?.match(/solid|dashed|dotted|double|groove|ridge|inset|outset/)?.at(0);
	const colorValue = value?.match(/(#[0-9a-f]{8}|#[0-9a-f]{6}|#[0-9a-f]{4}|#[0-9a-f]{3})$/i)?.at(0);

	const onInputChange = React.useMemo(() => (
		size?: string, style?: string, color?: string,
	) => {
		return onChange?.(`${size || ''}px ${style || ''} ${color || ''}`);
	}, [onChange]);

	return (
		<View centerV gap="sm" row spread>
			<InputBoolean value={isEnabled} onChange={() => onChange?.(isEnabled ? 'none' : undefined)} />
			{isEnabled && [
				<View centerV gapH="xs" row>
					Taille
					<InputNumber
						changeOnWheel
						max={100}
						min={1}
						onChange={v => onInputChange(v as string, styleValue, colorValue)}
						placeholder="Taille"
						style={{ width: 100 }}
						value={sizeValue as number | undefined}
						suffix="px"
					/>
					Style
					<Select
						onChange={v => onInputChange(sizeValue, v as string, colorValue)}
						options={[
							{ label: 'Solide', value: 'solid' },
							{ label: 'Tirets', value: 'dashed' },
							{ label: 'Points', value: 'dotted' },
							{ label: 'Double', value: 'double' },
							{ label: 'Groove', value: 'groove' },
							{ label: 'Ridge', value: 'ridge' },
							{ label: 'Inset', value: 'inset' },
							{ label: 'Outset', value: 'outset' },
						]}
						placeholder="Unité"
						style={{ width: 100 }}
						value={styleValue}
					/>
					Couleur
					<ColorPicker
						onChange={v => onInputChange(sizeValue, styleValue, v as string)}
						showText={false}
						value={colorValue}
						disabledAlpha={false}
					/>
				</View>,
			]}
		</View>
	);
};
const IFrameBorderInputRules = [{
	pattern: /none|^(\d+)+(%|px|vh|vw)+\s+(solid|dashed|dotted|double|groove|ridge|inset|outset)+\s+((#[0-9a-f]{8}|#[0-9a-f]{6}|#[0-9a-f]{4}|#[0-9a-f]{3})$)/i,
	message: 'Veuillez renseigner cette information',
}];

export default ShareForm;